import { ChessMate } from '@minigame-race/contracts';
import { useEffect, useState } from 'react';
import { color } from '../../theme.js';
import type { MinigameProps } from '../../race/types.js';

/**
 * Mate-in-2. Tap a piece, then tap where it goes. The server decides whether the
 * move is legal and whether it forces mate; we don't predict, because a wrong
 * move must visibly do nothing rather than flash a fake board.
 *
 * The board is drawn from White's perspective (a8 top-left … h1 bottom-right,
 * with White on the bottom two ranks).
 */

const GLYPH: Record<string, string> = {
  K: '♔', Q: '♕', R: '♖', B: '♗', N: '♘', P: '♙',
  k: '♚', q: '♛', r: '♜', b: '♝', n: '♞', p: '♟',
};

function isView(v: unknown): v is ChessMate.ChessMatePrivateView {
  return typeof v === 'object' && v !== null && (v as { kind?: string }).kind === 'chess-mate';
}

export function ChessMateGame({ view, emit }: MinigameProps) {
  const s = isView(view) ? view : null;
  const [from, setFrom] = useState<number | null>(null);
  const [shake, setShake] = useState(0);

  useEffect(() => {
    if (s?.hintWrong) {
      setShake((n) => n + 1);
      setFrom(null);
    }
  }, [s?.hintWrong, s?.attempts]);

  if (!s) return null;

  const pieceAt = (sq: number): string => s.pieces.find((p) => p.square === sq)?.piece ?? '';
  const isWhite = (sq: number): boolean => {
    const p = pieceAt(sq);
    return p !== '' && p === p.toUpperCase();
  };

  const onSquare = (sq: number): void => {
    if (s.isComplete) return;
    if (from === null) {
      if (isWhite(sq)) setFrom(sq);
      return;
    }
    if (sq === from) { setFrom(null); return; }
    if (isWhite(sq)) { setFrom(sq); return; } // reselect own piece
    emit({ type: 'move', from, to: sq });
    setFrom(null);
  };

  const [lf, lt] = s.lastEngineMove ?? [-1, -1];

  return (
    <div style={{ width: '100%', maxWidth: 'min(86cqmin,420px)', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'clamp(8px,2cqmin,14px)' }}>
      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 'clamp(13px,2.8cqmin,18px)', fontWeight: 700, color: s.hintWrong ? color.redBright : color.lime }}>
          {s.ply === 0 ? 'White to move — mate in 2' : 'Now finish it — mate in 1'}
        </span>
        {s.hintWrong && <span style={{ fontSize: 'clamp(10px,1.9cqmin,13px)', color: color.amber }}>Not forced. Try another move.</span>}
      </div>

      <div
        key={shake}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(8, 1fr)',
          width: '100%',
          aspectRatio: '1 / 1',
          borderRadius: 10,
          overflow: 'hidden',
          border: '1px solid rgba(166,255,46,.2)',
          animation: s.hintWrong ? 'rhBlinkRed .4s steps(1,end) 1' : undefined,
        }}
      >
        {Array.from({ length: 64 }, (_, sq) => {
          const r = Math.floor(sq / 8);
          const c = sq % 8;
          const dark = (r + c) % 2 === 1;
          const piece = pieceAt(sq);
          const selected = from === sq;
          const engineHi = sq === lf || sq === lt;
          return (
            <button
              key={sq}
              type="button"
              onClick={() => onSquare(sq)}
              style={{
                position: 'relative',
                aspectRatio: '1 / 1',
                border: 0,
                background: selected
                  ? 'rgba(166,255,46,.45)'
                  : engineHi
                    ? 'rgba(34,224,255,.25)'
                    : dark
                      ? '#2a3b1e'
                      : '#3f5230',
                display: 'grid',
                placeItems: 'center',
                cursor: s.isComplete ? 'default' : 'pointer',
                padding: 0,
                touchAction: 'manipulation',
              }}
            >
              {piece && (
                <span
                  style={{
                    fontSize: 'clamp(18px,6cqmin,40px)',
                    lineHeight: 1,
                    color: piece === piece.toUpperCase() ? '#fbfff5' : '#0c1408',
                    textShadow: piece === piece.toUpperCase() ? '0 1px 2px rgba(0,0,0,.6)' : '0 1px 1px rgba(255,255,255,.25)',
                    userSelect: 'none',
                  }}
                >
                  {GLYPH[piece]}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div style={{ textAlign: 'center', fontSize: 'clamp(9px,1.7cqmin,12px)', color: 'rgba(234,255,234,.4)' }}>
        Tap a white piece, then its destination.
      </div>
    </div>
  );
}
